// hooks
import { useEffect, useState } from "react";
import { useComponentProperties } from "../../../../Hooks/Display/useComponentProperties";
import useComponents, { useSelectedComponent } from "../../../../Hooks/Display/useComponents";
import { usePropsStoreKey, usePropsStoreValue } from "../../../../Hooks/PropStore/usePropsStore";

// components
import { Menu, MenuItem } from "@progress/kendo-react-layout";
import { toggleComponentHiddenState } from "../../../../API/Display/Display";
import { useSelectedPage } from "../../../../Hooks/Display/usePages";
import { useTranslation } from "react-i18next";

function ExtraOptionsMenu({ onReset, disabled }) {

    const PAGE_COMPONENT_TYPE_TO_RESET_MAP = {
        "Table": ["Heading", "Table"],
        "Cards": ["Heading", "Cards"],
        "Kanban": ["Heading", "Kanban"],
        "Calendar": ["Heading", "Calendar"],
        "List": ["Heading", "List"],
        "New Record": ["Form"],
        "Edit Record": ["Form"],
    }

    const [showToggleSimpleConfigOption, setShowToggleSimpleConfigOption] = useState(false);
    const [accessedFromDesigner, setAccessedFromDesigner] = usePropsStoreKey("accessedFromDesigner");

    const selectedPage = useSelectedPage();
    const selectedComponent = useSelectedComponent();
    const { hidden } = selectedComponent;
    const [{ components }, dispatch] = useComponents();

    const { t } = useTranslation("display/component_toolbar");

    useEffect(() => {
        setShowToggleSimpleConfigOption(accessedFromDesigner)
    }, [])

    // states
    const resetComponent = PAGE_COMPONENT_TYPE_TO_RESET_MAP[selectedPage.subPageType] && PAGE_COMPONENT_TYPE_TO_RESET_MAP[selectedPage.subPageType].includes(selectedComponent.componentType);
    const parentHidden = false;
    const isDeleteDisabled = false;
    const advancedMode = usePropsStoreValue("advancedMode");
    
    // functions
    function toggleSimpleConfig() {
        setAccessedFromDesigner(!accessedFromDesigner);
    }

    async function toggleComponentHidden() {
        await toggleComponentHiddenState(selectedPage._id, selectedComponent.id);
        dispatch({
            type: "TOGGLE_COMPONENT_HIDDEN",
            payload: selectedComponent.treeIndex
        });
    }

    function handleDelete() {
        dispatch({
            type: "TOGGLE_DELETE_COMPONENT_DIALOG",
            payload: selectedComponent.treeIndex
        })
    }

    function handleReset() {
        dispatch({
            type: "TOGGLE_RESET_COMPONENT_DIALOG",
            payload: selectedComponent.treeIndex
        })
    }

    function handleRevertChanges() {
        onReset();
    }

    function shouldHideBeHidden() {
        const hideableComponentTypes = [
            "Table", "Cards", "Kanban", "Calendar", "List", "Form", "Heading", "View"
        ];
    
        return !(hideableComponentTypes.includes(selectedComponent.componentType) && !advancedMode);
    };

    function shouldHideBeDisabled() {
        if (selectedComponent.hidden) return false;
        return accessedFromDesigner ? false : components.filter(component => !component.hidden).length < 2;
    }

    function shouldResetBeDisabled() {
        if (selectedComponent.hidden) return true;
        return disabled;
    }

    return (
        <Menu
            onSelect={(e) => e.item.data()}
            className="no-dropdown-in-menu three-dots"
        >
            <MenuItem
                cssClass="btn btn-secondary px-1"
                icon="more-horizontal g-0 m-0 ps-2"
                data={() => { }}
            >
                {showToggleSimpleConfigOption && (
                    <MenuItem
                        data={toggleSimpleConfig}
                        icon={
                            accessedFromDesigner
                                ? "checkbox"
                                : "checkbox-checked"
                        }
                        disabled={parentHidden}
                        text={
                            accessedFromDesigner
                                ? t("buttons.turn_on_simple_display_config")
                                : t("buttons.turn_off_simple_display_config")
                        }
                    ></MenuItem>
                )}
                {
                    shouldHideBeHidden() &&
                    <MenuItem
                        data={toggleComponentHidden}
                        icon="cancel-outline"
                        disabled={shouldHideBeDisabled()}
                        text={hidden ? t("buttons.show") : t("buttons.hide")}
                    ></MenuItem>
                }
                <MenuItem
                    icon="undo"
                    disabled={shouldResetBeDisabled()}
                    text={t("buttons.revert_changes")}
                    data={handleRevertChanges}
                ></MenuItem>
                {accessedFromDesigner && (
                    <MenuItem
                        data={resetComponent ? handleReset : handleDelete}
                        icon={resetComponent ? "reset" : "times"}
                        disabled={isDeleteDisabled || parentHidden}
                        text={resetComponent ? t("buttons.reset") : t("buttons.delete")}
                    ></MenuItem>
                )}
            </MenuItem>
        </Menu>
    )
}

export default ExtraOptionsMenu;